// Phase DP P1 fix — formatApiError extracted into its own pure module
// so component-tests (and the lib/api re-export) can both consume it
// without dragging axios's ESM bundle through Jest's CRA preset.
export function formatApiError(err) {
    const detail = err?.response?.data?.detail;
    if (detail == null) return err?.message || "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    // ``{message, field_errors}`` from /admin/rentals/{id}/deposit/collect
    // used to fall through to ``String(detail)`` and surface as
    // "[object Object]" in admin toasts. Compose a useful sentence
    // instead so the toast tells the admin exactly what's wrong.
    if (detail && typeof detail === "object") {
        const parts = [];
        if (typeof detail.message === "string" && detail.message) parts.push(detail.message);
        const fe = detail.field_errors;
        if (fe && typeof fe === "object") {
            const feParts = Object.entries(fe)
                .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
                .filter(Boolean);
            if (feParts.length) parts.push(feParts.join("; "));
        }
        if (parts.length) return parts.join(" — ");
    }
    return String(detail);
}

// When axios is called with ``responseType: "blob"``, error response
// bodies arrive as a Blob, NOT as parsed JSON — so ``formatApiError``
// alone sees ``err.response.data`` as a Blob and falls through to the
// generic ``err.message`` ("Request failed with status code 404"),
// hiding the useful ``detail`` the server returned.
//
// Use this helper anywhere the call sets ``responseType: "blob"`` (e.g.
// admin "View →" file-streaming handlers). It re-parses the blob into
// JSON, swaps it into ``err.response.data``, and then defers to the
// canonical ``formatApiError`` shape so all the existing fallthrough
// rules (string detail, 422 array, ``{message, field_errors}``, etc.)
// keep working.
export async function formatBlobApiError(err) {
    const data = err?.response?.data;
    if (data && typeof Blob !== "undefined" && data instanceof Blob) {
        try {
            // ``Blob.prototype.text()`` is implemented in modern browsers and
            // recent jsdom (≥16), but CRA's bundled jsdom returns "" in some
            // older versions. Falling back through ``Response`` covers both
            // — ``new Response(blob).text()`` is part of the Fetch polyfill
            // included with jsdom and works deterministically.
            let text = "";
            if (typeof data.text === "function") {
                text = await data.text();
            }
            if (!text && typeof Response !== "undefined") {
                text = await new Response(data).text();
            }
            if (text) {
                try {
                    err.response.data = JSON.parse(text);
                } catch (_jsonErr) {
                    err.response.data = { detail: text };
                }
            }
        } catch (_readErr) {
            // Blob unreadable — leave err.response.data as-is and let
            // formatApiError fall through to err.message.
        }
    }
    return formatApiError(err);
}
